<template>
  <v-col cols="12">
    <gg-card>
      <gg-card-header>
        Manage Git Repositories
      </gg-card-header>
      <gg-card-text>
        <v-data-table :headers="headers" :items="repositories">
          <template v-slot:item.name="{ item }">
            {{ item.name }}
            <v-icon small @click="showDetails(item)">mdi-information</v-icon>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-menu bottom offset-y min-width="180">
              <template v-slot:activator="{ on, attrs }">
                <gg-btn v-on="on" v-bind="attrs" color="primary">
                  Manage <v-icon right>mdi-chevron-down</v-icon>
                </gg-btn>
              </template>
              <v-list>
                <v-list-item @click="openEditModal(item)">
                  <v-list-item-title>
                    Edit
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="openDeleteModal(item)">
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </gg-card-text>
    </gg-card>

    <gg-modal v-if="basicInformationModal" :is-active="basicInformationModal" @close="hideDetails">
      <template #toolbar>
        Basic Information
      </template>
      <template #body>
        <v-row>
          <v-col cols="12">
            <b>Branch information</b>

            <div v-if="repositoryToShow.branch !== null">
              Checked-out Branch: <code>{{ repositoryToShow.branch }}</code>
            </div>
            <div v-else>
              No checked-out branch is available.
            </div>

            <div v-for="(value, name) in repositoryToShow.last_update" :key="value">
              <span>{{ name }}</span>
              <code>{{ value }}</code>
            </div>
          </v-col>

          <v-col cols="12">
            <b>Checkout URL</b>

            <div>
              <code v-for="item in repositoryToShow.clone_urls.read_write" :key="item">
                {{ item }}
              </code>
            </div>
          </v-col>

          <v-col cols="12">
            <b>Repository path</b>
            <div>
              <code>{{ repositoryToShow.repository_root }}</code>
            </div>
          </v-col>
        </v-row>
      </template>
      <template #actions>
        <gg-btn @click="hideDetails">Close</gg-btn>
      </template>
    </gg-modal>
    <gg-modal v-if="removeModal" :is-active="removeModal" @close="closeDeleteModal">
      <template #toolbar>
        Delete repository
      </template>
      <template #body>
        <v-row>
          <v-col cols="12">

            <p>
              <b>Are you sure that you want to remove repository from the list of cPanel-managed
              repositories?
              </b>
            </p>

            <p class="red--text">
              This action only removes the repository from the list of cPanel-managed repositories. The system will not
              delete the files. To completely delete the repository, manually delete the files after you remove it from
              the list.
            </p>

          </v-col>
        </v-row>
      </template>
      <template #actions>
        <gg-btn secondary @click="closeDeleteModal">Cancel</gg-btn>
        <gg-btn :loading="deleteLoading" :disabled="deleteLoading" @click="deleteRepository">Delete</gg-btn>
      </template>
    </gg-modal>
    <gg-modal v-if="editModal" :is-active="editModal" @close="closeEditModal">
      <template #toolbar>
        Edit repository
      </template>
      <template #body>
        <v-row>
          <v-col cols="12">
            <gg-text-field label="Repository Name"
                           v-model="editModel.name"
                           v-validate="'required'"
                           name="repository-name"
                           data-vv-as="Repository name"
                           :error-messages="errors.collect('repository-name')"
            />
          </v-col>
          <v-col cols="12" v-if="repositoryToShow.branch !== null">
            <gg-select label="Checked-Out Branch" v-model="editModel.branch" :items="repositoryToShow.available_branches" />
          </v-col>
          <v-col cols="12" v-else>
            <div>
              <b>Checked-Out Branch</b>
            </div>
            The system could not find any branches for this repository.
          </v-col>
        </v-row>
      </template>
      <template #actions>
        <gg-btn secondary @click="closeEditModal">Cancel</gg-btn>
        <gg-btn :loading="editLoading" :disabled="editLoading" @click="editRepository">Edit</gg-btn>
      </template>
    </gg-modal>
  </v-col>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'RepositoryTable',
  data: () => ({
    headers: [
      {
        text: 'Repository',
        value: 'name'
      },
      {
        text: 'Repository Path',
        value: 'repository_root'
      },
      {
        text: 'Actions',
        value: 'actions',
        align: 'center',
        sortable: false
      },
    ],
    editModel: {
      name: '',
      branch: ''
    },
    basicInformationModal: false,
    removeModal: false,
    editModal: false,
    repositoryToShow: {},
    repositoryToDelete: {},
    createLoading: false,
    deleteLoading: false,
    editLoading: false
  }),
  computed: {
    ...mapGetters({
      repositories: 'hosting_git/repositories'
    })
  },
  mounted () {
    this.getRepositories(this.$route.query.user)
  },
  methods: {
    ...mapActions({
      getRepositories: 'hosting_git/getRepositories',
      editRepo: 'hosting_git/editRepo',
      deleteRepo: 'hosting_git/deleteRepo',
      reportError: 'reportError'
    }),
    showDetails (item) {
      Object.assign(this.repositoryToShow, item)

      this.basicInformationModal = true
    },
    hideDetails () {
      this.basicInformationModal = false
      this.repositoryToShow = {}
    },
    openDeleteModal (item) {
      Object.assign(this.repositoryToDelete, item)

      this.removeModal = true
    },
    closeDeleteModal () {
      this.removeModal = false
      this.repositoryToDelete = {}
    },
    async deleteRepository () {
      try {
        this.deleteLoading = true

        await this.deleteRepo({
          repository_root: this.repositoryToDelete.repository_root,
          _username: this.$route.query.user,
        })
        await this.getRepositories(this.$route.query.user)
        this.closeDeleteModal()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.deleteLoading = false
      }
    },
    openEditModal (item) {
      Object.assign(this.repositoryToShow, item)
      this.editModel.name = this.repositoryToShow.name
      this.editModel.branch = this.repositoryToShow.branch

      this.editModal = true
    },
    closeEditModal () {
      this.editModal = false
      this.repositoryToShow = {}
    },
    async editRepository () {
      try {
        const valid = await this.$validator.validateAll()

        if (!valid) { return }

        this.editLoading = true

        await this.editRepo({
          repository_root: this.repositoryToShow.repository_root,
          name: this.editModel.name,
          branch: this.editModel.branch ?? undefined,
          _username: this.$route.query.user,
        })
        await this.getRepositories(this.$route.query.user)
        this.closeEditModal()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.editLoading = false
      }
    },
  }
}
</script>

<style scoped>

</style>
